import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import logoImg from '../../images/logo-no-tagline.png';

/**
 * PPL-36 — Branded Firebase Auth action landing.
 *
 * Firebase emails (verifyEmail / resetPassword / recoverEmail) redirect to
 *   `actionCodeSettings.url` with query params:
 *     ?mode=verifyEmail&oobCode=...&apiKey=...&lang=en
 *
 * For `verifyEmail` we apply the oobCode, reload the current user, and then
 * land the user on a continueUrl (default /f8chat). Other modes are routed
 * back into the existing flows.
 *
 * NOTE: Firebase Console → Authentication → Settings → Authorized domains
 *       includes `pplkpr.ai` (and any preview/staging domain) for the
 *       email link to be accepted. See PR body "Manual follow-up".
 */

const VerifyEmailAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applyVerification, user } = useAuth();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrlRaw = searchParams.get('continueUrl');
  const isRelativeContinue = continueUrlRaw && continueUrlRaw.startsWith('/') && !continueUrlRaw.startsWith('//');
  const continueUrl = isRelativeContinue ? continueUrlRaw : null;

  const [status, setStatus] = useState('pending'); // pending | success | error | landed
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Non-verify modes: just hand off to the corresponding flows so we don't
    // accidentally swallow password-reset or email-recovery links sent through
    // the same actionCodeSettings.url.
    if (mode === 'resetPassword') {
      // Forward through to the standard login → reset surface, preserving
      // the oobCode if a future feature wants to consume it.
      const qs = new URLSearchParams();
      if (oobCode) qs.set('oobCode', oobCode);
      navigate(`/login?${qs.toString()}`, { replace: true });
      return;
    }

    // No action params at all: this is the normal landing AFTER Firebase's
    // hosted handler (the email template's action URL, app.pplkpr.ai/__/auth/
    // action) has already applied the oobCode and redirected here via
    // continueUrl. The action succeeded on the previous screen — showing an
    // error here reads as a failed verification (PPL-241 QA).
    if (!mode && !oobCode) {
      setStatus('landed');
      return;
    }

    if (mode !== 'verifyEmail') {
      setStatus('error');
      setErrorMessage('this link is missing a recognised action.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await applyVerification(oobCode);
        if (cancelled) return;
        setStatus('success');
      } catch (err) {
        if (cancelled) return;
        console.error('Email verification failed:', err);
        setStatus('error');
        setErrorMessage(err?.message || 'this verification link is invalid or has expired.');
      }
    })();

    return () => { cancelled = true; };
    // applyVerification is stable for the lifetime of the AuthProvider; we
    // only want this to run on initial mount + oobCode change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oobCode, mode]);

  const handleContinue = () => {
    if (continueUrl) {
      navigate(continueUrl, { replace: true });
    } else if (user) {
      navigate('/f8chat', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(800px 500px at 50% 30%, rgba(124, 58, 237, 0.18), transparent 60%), #0e0817',
      padding: '40px 20px',
      color: '#fff',
    }}>
      <div style={{
        width: '100%', maxWidth: 460,
        padding: '32px 28px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 20,
        backdropFilter: 'blur(10px)',
        textAlign: 'center',
      }}>
        <img
          src={logoImg}
          alt="pplkpr"
          style={{ height: 36, width: 'auto', display: 'block', margin: '0 auto 24px' }}
        />

        {status === 'pending' && (
          <>
            <Spinner />
            <h1 style={titleStyle}>verifying your email…</h1>
            <p style={bodyStyle}>this should only take a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <SuccessIcon />
            <h1 style={titleStyle}>email verified</h1>
            <p style={bodyStyle}>
              thanks for confirming. you&apos;re all set to keep building better relationships.
            </p>
            <button
              type="button"
              onClick={handleContinue}
              style={ctaButton}
            >
              {continueUrl ? 'continue →' : 'open pplkpr →'}
            </button>
          </>
        )}

        {status === 'landed' && (
          <>
            <SuccessIcon />
            <h1 style={titleStyle}>you&apos;re all set</h1>
            <p style={bodyStyle}>
              if you just clicked a link from a pplkpr email, that&apos;s done — you can
              carry on right where you left off.
            </p>
            <button
              type="button"
              onClick={handleContinue}
              style={ctaButton}
            >
              continue →
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon />
            <h1 style={titleStyle}>verification failed</h1>
            <p style={bodyStyle}>{errorMessage}</p>
            <p style={{ ...bodyStyle, marginTop: 16, fontSize: 13 }}>
              try logging in and resending the verification email from your account.
            </p>
            <Link to="/login" style={{ ...ctaButton, display: 'inline-block', textDecoration: 'none', marginTop: 24 }}>
              go to login
            </Link>
          </>
        )}

        <div style={{ marginTop: 28, fontSize: 11.5, color: 'rgba(220, 200, 240, 0.45)' }}>
          need help? <a href="mailto:support@pplkpr.ai" style={{ color: '#a78bfa' }}>support@pplkpr.ai</a>
        </div>
      </div>
    </div>
  );
};

// ─── Styles ─────────────────────────────────────────────────
const titleStyle = {
  margin: '20px 0 8px',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '-0.025em',
  color: '#fff',
};

const bodyStyle = {
  margin: 0,
  fontSize: 14.5,
  lineHeight: 1.55,
  color: 'rgba(220, 200, 240, 0.7)',
};

const ctaButton = {
  appearance: 'none', border: 0, cursor: 'pointer',
  marginTop: 24, padding: '14px 22px', borderRadius: 14,
  background: 'linear-gradient(135deg, #d946ef 0%, #8b5cf6 100%)',
  color: '#fff', fontSize: 14.5, fontWeight: 700, fontFamily: 'inherit',
  boxShadow: '0 12px 32px rgba(217, 70, 239, 0.4)',
};

// ─── Icons ──────────────────────────────────────────────────
function Spinner() {
  return (
    <>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        border: '3px solid rgba(217, 70, 239, 0.18)',
        borderTopColor: '#d946ef',
        margin: '0 auto',
        animation: 'verifySpin 0.8s linear infinite',
      }} />
      <style>{`
        @keyframes verifySpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

function SuccessIcon() {
  return (
    <div style={{
      width: 56, height: 56, borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.22), rgba(20, 184, 166, 0.22))',
      border: '1px solid rgba(34, 197, 94, 0.4)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto',
    }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="m5 12 5 5L20 7" stroke="#86efac" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function ErrorIcon() {
  return (
    <div style={{
      width: 56, height: 56, borderRadius: '50%',
      background: 'rgba(239, 68, 68, 0.15)',
      border: '1px solid rgba(239, 68, 68, 0.4)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto',
    }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#fca5a5" strokeWidth="1.7" />
        <path d="M12 8v5M12 16v.5" stroke="#fca5a5" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export default VerifyEmailAction;
